import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Switch,
} from '@workingclock/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCookieConsent } from '../../contexts/cookie-consent-context';
import { ESSENTIAL_ONLY } from '../../lib/consent';
import type { ConsentPreferences, ConsentPurpose } from '../../lib/consent';

/** Granular consent controls, reachable from both the banner and the footer's "Cookie preferences" link. */
export function CookiePreferencesDialog() {
  const { t } = useTranslation('marketing');
  const { isPreferencesOpen, closePreferences, savePreferences, consent } = useCookieConsent();
  const [draft, setDraft] = useState<ConsentPreferences>(consent?.preferences ?? ESSENTIAL_ONLY);

  useEffect(() => {
    if (isPreferencesOpen) {
      setDraft(consent?.preferences ?? ESSENTIAL_ONLY);
    }
  }, [isPreferencesOpen, consent]);

  const toggles: Array<{ purpose: ConsentPurpose; label: string; description: string }> = [
    {
      purpose: 'non_essential_cookies',
      label: t('cookieConsent.preferences.toggles.functional.label'),
      description: t('cookieConsent.preferences.toggles.functional.description'),
    },
    {
      purpose: 'analytics',
      label: t('cookieConsent.preferences.toggles.analytics.label'),
      description: t('cookieConsent.preferences.toggles.analytics.description'),
    },
    {
      purpose: 'marketing_email',
      label: t('cookieConsent.preferences.toggles.marketingEmail.label'),
      description: t('cookieConsent.preferences.toggles.marketingEmail.description'),
    },
  ];

  return (
    <Dialog open={isPreferencesOpen} onOpenChange={(open) => !open && closePreferences()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('cookieConsent.preferences.title')}</DialogTitle>
          <DialogDescription>{t('cookieConsent.preferences.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
            <div>
              <p className="text-label font-medium text-ink">{t('cookieConsent.preferences.essential.label')}</p>
              <p className="mt-1 text-body text-muted">{t('cookieConsent.preferences.essential.description')}</p>
            </div>
            <Switch
              checked
              disabled
              onCheckedChange={() => {}}
              aria-label={t('cookieConsent.preferences.essential.ariaLabel')}
            />
          </div>

          {toggles.map((toggle) => (
            <div
              key={toggle.purpose}
              className="flex items-start justify-between gap-4 rounded-md border border-border p-3"
            >
              <div>
                <Label htmlFor={`consent-${toggle.purpose}`}>{toggle.label}</Label>
                <p className="mt-1 text-body text-muted">{toggle.description}</p>
              </div>
              <Switch
                id={`consent-${toggle.purpose}`}
                checked={draft[toggle.purpose]}
                onCheckedChange={(checked) => setDraft((prev) => ({ ...prev, [toggle.purpose]: checked }))}
              />
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => savePreferences(ESSENTIAL_ONLY)}>
            {t('cookieConsent.preferences.reject')}
          </Button>
          <Button onClick={() => savePreferences(draft)}>{t('cookieConsent.preferences.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
